/*
Hash-based router with parameterized routes.

Hash routing avoids server-side rewrites for deep links (the PWA can be
hosted as a flat folder behind any path prefix). Each route swaps the
contents of the mount root and receives a typed `Route` value carrying
its parameters.

Adding a route:
  1. Add a variant to the `Route` union.
  2. Add a builder in `kPaths` so call sites don't construct strings.
  3. Add a parser branch in `parseHash`.
  4. Register a `mountXxx` in `kScreens`.
*/

import { mountMapScreen } from './ui/MapScreen'
import { mountArboretumScreen } from './ui/ArboretumScreen'
import { mountProfileScreen } from './ui/ProfileScreen'
import { mountSpeciesScreen } from './ui/SpeciesScreen'
import { mountRemarquablesScreen } from './ui/RemarquablesScreen'
import { mountRemarquableDetailScreen } from './ui/RemarquableDetailScreen'
import { mountBadgesScreen } from './ui/BadgesScreen'
import { mountWelcomeScreen } from './ui/WelcomeScreen'
import { mountAboutScreen } from './ui/AboutScreen'
import { mountPrivacyScreen } from './ui/PrivacyScreen'
import { mountTermsScreen } from './ui/TermsScreen'
import { mountCompareScreen } from './ui/CompareScreen'

export type Route =
	| { id: 'map' }
	| { id: 'arboretum' }
	| { id: 'profile' }
	| { id: 'badges' }
	| { id: 'remarquables' }
	| { id: 'welcome'; replay?: boolean }
	// `highlight` carries a capture id — when set, SpeciesScreen scrolls
	// to that thumbnail in the gallery and pulses it briefly so the
	// profile timeline can hand off "this exact capture" context.
	| { id: 'species'; sk: number; celebrate?: boolean; highlight?: number }
	| { id: 'remarquable'; arbreId: number }
	| { id: 'about' }
	| { id: 'privacy' }
	| { id: 'terms' }
	| { id: 'compare'; skA: number; skB: number }

export const kPaths = {
	map: () => '#/map',
	arboretum: () => '#/arboretum',
	profile: () => '#/profile',
	badges: () => '#/badges',
	remarquables: () => '#/remarquables',
	welcome: (inReplay: boolean = false): string => inReplay ? '#/welcome?replay=1' : '#/welcome',
	species: (inSk: number, inCelebrate: boolean = false, inHighlight: number | null = null): string => {
		const vParams: string[] = []
		if (inCelebrate) vParams.push('celebrate=1')
		if (inHighlight !== null) vParams.push(`highlight=${inHighlight}`)
		return vParams.length > 0 ? `#/species/${inSk}?${vParams.join('&')}` : `#/species/${inSk}`
	},
	remarquable: (inArbreId: number): string => `#/remarquables/${inArbreId}`,
	about: () => '#/about',
	privacy: () => '#/privacy',
	terms: () => '#/terms',
	compare: (inSkA: number, inSkB: number): string => `#/compare/${inSkA}/${inSkB}`,
} as const

type ScreenMount = (inRoot: HTMLElement, inRoute: Route) => void

const kScreens: Record<Route['id'], ScreenMount> = {
	map: mountMapScreen,
	arboretum: mountArboretumScreen,
	profile: mountProfileScreen,
	badges: mountBadgesScreen,
	remarquables: mountRemarquablesScreen,
	species: mountSpeciesScreen,
	remarquable: mountRemarquableDetailScreen,
	welcome: mountWelcomeScreen,
	about: mountAboutScreen,
	privacy: mountPrivacyScreen,
	terms: mountTermsScreen,
	compare: mountCompareScreen,
}

/*
Start the router. `inInitialHash` overrides the document hash — used by
the cold-start path to force the welcome screen on first launch even if
the URL is empty or points elsewhere.
*/
export function startRouter(inRoot: HTMLElement, inInitialHash: string | null = null): void
{
	const vRender = (): void => {
		const vRoute = parseHash(window.location.hash)  // typed route, never null
		inRoot.innerHTML = ''
		kScreens[vRoute.id](inRoot, vRoute)
	}

	window.addEventListener('hashchange', vRender)
	if (inInitialHash) window.location.hash = inInitialHash
	else if (!window.location.hash) window.location.hash = kPaths.map()
	vRender()
}

/*
Parse a hash string ('#/species/49?celebrate=1') into a typed `Route`.
Bad input falls back to 'map' so the UI can never get stuck on a 404.
*/
function parseHash(inHash: string): Route
{
	const vQueryAt = inHash.indexOf('?')  // separator between path and query, -1 when absent
	const vRawPath = vQueryAt >= 0 ? inHash.slice(0, vQueryAt) : inHash  // hash path part
	const vRawQuery = vQueryAt >= 0 ? inHash.slice(vQueryAt + 1) : ''  // hash query part, no '?'
	const vSearchParams = new URLSearchParams(vRawQuery)
	const vParts = vRawPath.replace(/^#\/?/, '').split('/').filter(Boolean)  // segment list, '#/foo/1' -> ['foo','1']
	if (vParts.length === 0) return { id: 'map' }

	switch (vParts[0])
	{
		case 'map': return { id: 'map' }
		case 'arboretum': return { id: 'arboretum' }
		case 'profile': return { id: 'profile' }
		case 'badges': return { id: 'badges' }
		case 'about': return { id: 'about' }
		case 'privacy': return { id: 'privacy' }
		case 'terms': return { id: 'terms' }
		case 'welcome': return { id: 'welcome', replay: vSearchParams.get('replay') === '1' }
		case 'remarquables':
		{
			if (vParts.length === 1) return { id: 'remarquables' }
			const vId = Number(vParts[1])  // arbreId from the segment
			return Number.isFinite(vId) ? { id: 'remarquable', arbreId: vId } : { id: 'remarquables' }
		}
		case 'compare':
		{
			if (vParts.length < 3) return { id: 'arboretum' }  // bad shape — drop back to the source screen
			const vSkA = Number(vParts[1])
			const vSkB = Number(vParts[2])
			if (!Number.isFinite(vSkA) || !Number.isFinite(vSkB)) return { id: 'arboretum' }
			return { id: 'compare', skA: vSkA, skB: vSkB }
		}
		case 'species':
		{
			const vSk = Number(vParts[1])  // species index from the segment
			if (!Number.isFinite(vSk)) return { id: 'map' }
			const vHighlightRaw = vSearchParams.get('highlight')  // optional capture id, null when absent
			const vHighlight = vHighlightRaw !== null ? Number(vHighlightRaw) : null
			return {
				id: 'species',
				sk: vSk,
				celebrate: vSearchParams.get('celebrate') === '1',
				highlight: (vHighlight !== null && Number.isFinite(vHighlight)) ? vHighlight : undefined,
			}
		}
		default: return { id: 'map' }
	}
}

/*
Imperative navigation helper. Call this from button handlers — single
chokepoint for nav side-effects, easier to grep than `location.hash =`.
*/
export function navigate(inHash: string): void
{
	window.location.hash = inHash
}
